/**
 * NewItemsNotificationBanner Component
 * 
 * Displays a notification banner when new items are created by other users.
 * Shows the count of new items and provides a refresh button.
 */

import React from 'react';
import { RefreshCw, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

export interface NewItemsNotificationBannerProps {
  /** Number of new items */
  newItemCount: number;
  /** Label for the type of items (e.g., "items", "dialogs", "documents") */
  itemLabel: string;
  /** Callback to refresh data */
  onRefresh: () => void;
  /** Callback to dismiss notification */
  onDismiss: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Whether refresh is in progress */
  isRefreshing?: boolean;
}

export function NewItemsNotificationBanner({
  newItemCount,
  itemLabel,
  onRefresh,
  onDismiss,
  className,
  isRefreshing = false,
}: NewItemsNotificationBannerProps) {
  const { t } = usePlatformTranslation();

  // Don't render if no new items
  if (newItemCount === 0) {
    return null;
  }

  const plural = newItemCount !== 1;
  const message = plural
    ? `${newItemCount} new ${itemLabel} available`
    : `${newItemCount} new ${itemLabel.replace(/s$/, '')} available`;

  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-lg py-2 px-4 gap-3 text-sm',
        'bg-green-50 dark:bg-green-950/30',
        'border border-green-200 dark:border-green-800',
        'shadow-sm',
        className
      )}
    >
      {/* Left side - New items info */}
      <div className="flex items-center gap-3">
        {/* Plus icon */}
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50">
          <Plus className="w-4 h-4 text-green-600 dark:text-green-400" />
        </div>

        {/* Message */}
        <div className="flex flex-col">
          <span className="font-medium text-green-900 dark:text-green-100">
            {message}
          </span>
          <span className="text-green-700 dark:text-green-400 text-xs">
            Click refresh to see the latest changes
          </span>
        </div>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2">
        {/* Refresh button */}
        <Button
          variant="default"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="h-8 px-3 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white"
        >
          <RefreshCw className={cn('w-4 h-4 mr-1.5', isRefreshing && 'animate-spin')} />
          <span>Refresh</span>
        </Button>

        {/* Dismiss button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="h-7 w-7 p-0 text-green-700 dark:text-green-400 hover:text-green-900 dark:hover:text-green-200 hover:bg-green-200/50 dark:hover:bg-green-800/50"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default NewItemsNotificationBanner;
